/* Progress: XP, level, streak, last 14 days, per-module progress, badges and personal records. */
import { useEffect, useRef, useState } from 'react';
import { useGo } from '../../app/navigation';
import {
  BADGES,
  type BadgeId,
  levelInfo,
  levelState,
  MODULE_IDS,
  resetProgress,
  streak,
  todayXp,
  useProgress,
  useSettings,
} from '../../core/store';
import { noteName } from '../../core/theory';
import { toast } from '../../core/toast';
import { dateKey, scrollTop } from '../../core/util';
import { Btn } from '../../ui/Button';
import { Icon } from '../../ui/icons';
import { modStyle, vars } from '../../ui/style';
import { ProgressBar } from '../../ui/widgets';
import { MODULE_INFO } from '../module/info';
import { moduleStats } from '../module/ModulePage';
import { MODULE_DEFS } from '../registry';

const DOW = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

function ActivityChart() {
  const progress = useProgress();
  const settings = useSettings();
  const [tip, setTip] = useState<{ text: string; left: number; top: number } | null>(null);
  // drawn at the card's own width so the labels keep their size on a phone and on a wide screen
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setW(Math.max(280, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const days: { key: string; label: string; dow: string; v: number }[] = [];
  const d = new Date();
  d.setDate(d.getDate() - 13);
  for (let i = 0; i < 14; i++) {
    const key = dateKey(d);
    days.push({
      key,
      label: `${d.getDate()}.${d.getMonth() + 1}`,
      dow: DOW[d.getDay()] as string,
      v: progress.days[key] || 0,
    });
    d.setDate(d.getDate() + 1);
  }
  const goal = settings.dailyGoal || 60;
  const max = Math.max(goal * 1.25, ...days.map((x) => x.v));
  const H = 210;
  const padL = 34;
  const padR = 8;
  const padT = 14;
  const padB = 34;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const step = plotW / days.length;
  const bw = Math.max(8, step - 10);
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  const ticks = [0, Math.round(max / 2 / 10) * 10, Math.round(max / 10) * 10].filter(
    (v, i, a) => a.indexOf(v) === i && v <= max,
  );
  const empty = days.every((x) => !x.v);
  return (
    <div className="chart-card">
      <div className="group-head">
        <h2>Son 14 gün</h2>
        <p>
          {empty
            ? 'Henüz XP kazanmadın. İlk dersini ya da antrenmanını bitirince günlük çubuklar burada görünecek.'
            : 'Günlük kazandığın XP. Kesikli çizgi günlük hedefin.'}
        </p>
      </div>
      <div style={{ position: 'relative' }} ref={box}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width={W}
          height={H}
          className="act-svg"
          role="img"
          aria-label="Son 14 günün XP grafiği"
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} className="act-grid" />
              <text x={padL - 6} y={y(t) + 3.5} className="act-lbl" textAnchor="end">
                {t}
              </text>
            </g>
          ))}
          <line x1={padL} x2={W - padR} y1={y(goal)} y2={y(goal)} className="act-goal" />
          <text x={W - padR} y={y(goal) - 5} className="act-lbl" textAnchor="end">
            hedef {goal}
          </text>
          {days.map((day, i) => {
            const cx = padL + step * i + step / 2;
            const x0 = cx - bw / 2;
            const isToday = i === days.length - 1;
            let bar = null;
            if (day.v > 0) {
              const top = y(day.v);
              const base = padT + plotH;
              const r = Math.min(4, (base - top) / 2, bw / 2);
              const path = `M${x0} ${base}V${top + r}Q${x0} ${top} ${x0 + r} ${top}H${x0 + bw - r}Q${x0 + bw} ${top} ${x0 + bw} ${top + r}V${base}Z`;
              bar = <path d={path} className={`act-bar${isToday ? ' today' : ''}`} />;
            }
            // today and every 2nd (narrow: 3rd) day before it
            const labelled = (days.length - 1 - i) % (W < 460 ? 3 : 2) === 0;
            return (
              <g key={day.key}>
                {bar}
                {labelled ? (
                  <>
                    <text x={cx} y={H - 18} className="act-lbl" textAnchor="middle">
                      {isToday ? 'Bugün' : day.dow}
                    </text>
                    <text x={cx} y={H - 5} className="act-lbl" textAnchor="middle">
                      {day.label}
                    </text>
                  </>
                ) : null}
                <rect
                  x={padL + step * i}
                  y={padT}
                  width={step}
                  height={plotH}
                  fill="transparent"
                  onPointerEnter={(e) => {
                    const svg = (e.currentTarget as SVGRectElement).ownerSVGElement;
                    const r = svg?.getBoundingClientRect();
                    if (!r) return;
                    setTip({
                      text: `${day.dow} ${day.label}: ${day.v} XP`,
                      left: (cx / W) * r.width,
                      top: (y(Math.max(day.v, 0)) / H) * r.height,
                    });
                  }}
                  onPointerLeave={() => setTip(null)}
                />
              </g>
            );
          })}
        </svg>
        <div
          className="act-tip"
          hidden={!tip}
          style={{
            position: 'absolute',
            pointerEvents: 'none',
            background: 'var(--ink)',
            color: 'var(--surface)',
            padding: '5px 9px',
            borderRadius: '8px',
            fontSize: '12.5px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transform: 'translate(-50%, -110%)',
            left: tip ? `${tip.left}px` : undefined,
            top: tip ? `${tip.top}px` : undefined,
          }}
        >
          {tip?.text}
        </div>
      </div>
      <table
        className="sr-only"
        style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
      >
        <tbody>
          {days.map((x) => (
            <tr key={x.key}>
              <td>{x.key}</td>
              <td>{x.v} XP</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProgressPage() {
  const go = useGo();
  const progress = useProgress();
  const settings = useSettings();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lv = levelInfo();
  const badges = Object.keys(BADGES) as BadgeId[];
  const got = badges.filter((b) => progress.badges[b]);
  const recs = progress.records;
  const sabit = recs['vokal.sabit'] as number | undefined;
  const sss = recs['vokal.sss'] as number | undefined;
  const hasRange = settings.voiceLow != null && settings.voiceHigh != null;
  return (
    <div style={vars({ '--mod': 'var(--accent)' })}>
      <header className="mod-head" style={{ marginBottom: '22px' }}>
        <div className="mod-icon">
          <Icon name="chart" />
        </div>
        <div className="mod-titles">
          <h1>İlerleme</h1>
          <p>Ne kadar çalıştın, nerede güçlüsün, sırada ne var.</p>
        </div>
      </header>
      <div className="prog-top">
        <div className="stat">
          <span className="stat-k">Toplam XP</span>
          <span className="stat-v">{progress.xp}</span>
          <span className="stat-s">Bugün: {todayXp()}</span>
        </div>
        <div className="stat">
          <span className="stat-k">Seviye {lv.n}</span>
          <span className="stat-v">{lv.name}</span>
          <ProgressBar frac={lv.frac} />
          <span className="stat-s">{lv.next ? `${lv.to - progress.xp} XP sonra: ${lv.next}` : 'En üst seviye'}</span>
        </div>
        <div className="stat">
          <span className="stat-k">Çalışma serisi</span>
          <span className="stat-v">{streak()} gün</span>
          <span className="stat-s">{Object.keys(progress.days).length} gün çalıştın</span>
        </div>
        <div className="stat">
          <span className="stat-k">Rozetler</span>
          <span className="stat-v">
            {got.length}/{badges.length}
          </span>
          <span className="stat-s">Aşağıda tümü</span>
        </div>
      </div>
      <section className="sec">
        <ActivityChart />
      </section>
      <section className="sec">
        <h2 className="sec-title">Bölümler</h2>
        <div className="mod-progress">
          {MODULE_IDS.map((m) => {
            const inf = MODULE_INFO[m];
            const defs = MODULE_DEFS[m];
            const st = moduleStats(m, defs.levels);
            return (
              <button
                key={m}
                type="button"
                className="mp-card"
                style={modStyle(m, { textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', font: 'inherit' })}
                onClick={() => go(m)}
              >
                <div className="mp-head">
                  <Icon name={inf.icon} />
                  <span>{inf.title}</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '12.5px',
                      color: 'var(--muted)',
                    }}
                  >
                    {progress.modXp[m] || 0} XP
                  </span>
                </div>
                <div className="mp-row">
                  <span>Dersler</span>
                  <ProgressBar frac={st.done / Math.max(1, st.total)} />
                  <span>
                    {st.done}/{st.total}
                  </span>
                </div>
                <div className="mp-row">
                  <span>Yıldızlar</span>
                  <ProgressBar frac={st.stars / Math.max(1, st.starsTotal)} />
                  <span>
                    {st.stars}/{st.starsTotal}
                  </span>
                </div>
                {defs.challenges.map((c) => {
                  const x = levelState(c.key);
                  return x ? (
                    <div className="mp-row" key={c.key}>
                      <span>Rekor</span>
                      <span>{c.title}</span>
                      <span>{x.best || 0}</span>
                    </div>
                  ) : null;
                })}
              </button>
            );
          })}
        </div>
      </section>
      {sabit != null || sss != null || hasRange ? (
        <section className="sec">
          <h2 className="sec-title">Kişisel ölçümler</h2>
          <div className="prog-top">
            {sabit != null ? (
              <div className="stat">
                <span className="stat-k">En uzun sabit ses</span>
                <span className="stat-v">{sabit} sn</span>
              </div>
            ) : null}
            {sss != null ? (
              <div className="stat">
                <span className="stat-k">En uzun "sss"</span>
                <span className="stat-v">{sss} sn</span>
              </div>
            ) : null}
            {hasRange ? (
              <div className="stat">
                <span className="stat-k">Ses aralığın</span>
                <span className="stat-v" style={{ fontSize: '20px' }}>
                  {noteName(settings.voiceLow as number)} – {noteName(settings.voiceHigh as number)}
                </span>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
      <section className="sec">
        <h2 className="sec-title">Rozetler</h2>
        <div className="badge-grid">
          {badges.map((id) => {
            const b = BADGES[id];
            const on = !!progress.badges[id];
            return (
              <div key={id} className={`badge${on ? ' got' : ''}`}>
                <span className="bd-ic">
                  <Icon name={on ? 'award' : 'lock'} />
                </span>
                <div>
                  <div className="bd-t">{b.name}</div>
                  <div className="bd-d">{b.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section className="sec">
        <h2 className="sec-title">Veriler</h2>
        <p className="mb-note" style={{ maxWidth: '68ch', marginBottom: '10px' }}>
          İlerlemen yalnızca bu tarayıcıda saklanır. Başka bir cihazda ya da gizli pencerede sıfırdan başlar.
        </p>
        <Btn kind="ghost" icon="replay" onClick={() => setConfirmOpen(true)}>
          İlerlemeyi sıfırla
        </Btn>
        {confirmOpen ? (
          <div className="panel" style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p>Bütün XP, dersler, yıldızlar, rozetler ve rekorlar silinecek. Ayarların korunur. Emin misin?</p>
            <div className="sc-actions">
              <Btn
                kind="danger"
                onClick={() => {
                  resetProgress();
                  toast('İlerleme sıfırlandı.');
                  setConfirmOpen(false);
                  scrollTop();
                }}
              >
                Evet, sıfırla
              </Btn>
              <Btn kind="ghost" onClick={() => setConfirmOpen(false)}>
                Vazgeç
              </Btn>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
